import { expValEquals } from '@confluence/feature-experiments';

import { type Principal, type Role, type GeneralAccessLevel } from '../controlled/commonTypes.tsx';

import type { QueriesState } from './Controller.tsx';
import type { MasterState } from './masterState.ts';

/** Data about a principal that you might want to look up by principal id */
export type PrincipalsMapEntry = {
	role: Role;
	principal: Principal;
	isNewOrUpdatedPrincipal: boolean;
};

export type PrincipalsMap = Map<string, PrincipalsMapEntry>;

/**
 * Builds a map that translates a principal ID to their profile and effective
 * permissions.
 */
export function makePrincipalsMap(
	{
		specificAccessDelta: { userPickerStagedAdditions, removals, reassignments, removeAll },
		generalAccessDelta,
	}: MasterState,
	{ contentRestrictions, currentUser, principalsPendingAccess }: QueriesState,
	{
		shouldIncludeUserPickerStagedAdditions,
		stagedGeneralAccessLevel,
		persistenceMode = 'live',
	}: {
		shouldIncludeUserPickerStagedAdditions: boolean;
		stagedGeneralAccessLevel: GeneralAccessLevel;
		persistenceMode?: 'staged' | 'live';
	},
): PrincipalsMap {
	const map = new Map<string, PrincipalsMapEntry>();

	// Ensure the current user is added to the principal map for these access levels.
	// This ensures the specific access list includes the current user when general access changes are staged.
	if (
		!(generalAccessDelta === 'none') &&
		(stagedGeneralAccessLevel === 'open-can-view' || stagedGeneralAccessLevel === 'restricted')
	) {
		map.set(currentUser.id, {
			role: 'can-edit',
			principal: currentUser,
			isNewOrUpdatedPrincipal: false,
		});
	}

	// Start with all the pre-existing principals
	for (const { principal, role } of contentRestrictions.principals) {
		map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: false });
	}

	// Include principals pending access
	for (const { principal, role } of principalsPendingAccess) {
		map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: false });
	}

	// Persistence specific access experiment + staged mode flow:
	// When in staged mode with the experiment enabled, staged additions should be visible
	// immediately after clicking "Add" (before the final Publish/Save action).
	// This is different from the control group, where staged additions are only materialized
	// when the user clicks the final Save/Share button.
	const isStagedModeExperiment =
		shouldIncludeUserPickerStagedAdditions &&
		persistenceMode === 'staged' &&
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment');

	// Staged mode experiment flow:
	// Add staged additions to the map first so that reassignments can be applied to them.
	// `removeAll` is applied later and should remove staged additions too.
	if (isStagedModeExperiment) {
		for (const { principal, role } of userPickerStagedAdditions) {
			map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: true });
		}
	}

	// Update the roles for any in the reassignments list
	for (const [id, role] of reassignments) {
		const existingEntry = map.get(id);
		if (existingEntry) {
			map.set(id, { ...existingEntry, role, isNewOrUpdatedPrincipal: true });
		}
	}

	// Delete all the ones removed in this session
	for (const id of removals) {
		map.delete(id);
	}

	// Remove all principals.
	// In staged+experiment flow, this should remove staged additions too (they are the staged list).
	if (removeAll) {
		for (const { principal } of contentRestrictions.principals) {
			if (principal.id !== currentUser.id) {
				map.delete(principal.id);
			}
		}
		for (const { principal } of principalsPendingAccess) {
			if (principal.id !== currentUser.id) {
				map.delete(principal.id);
			}
		}
		if (isStagedModeExperiment) {
			for (const { principal } of userPickerStagedAdditions) {
				if (principal.id !== currentUser.id) {
					map.delete(principal.id);
				}
			}
		}
	}

	// Standard CTA flow (control group or live mode):
	// Staged additions are only materialized when the user clicks the final Save/Share button.
	// This ensures that in the control group, principals aren't shown in the list until save time.
	const isStandardCtaFlow = shouldIncludeUserPickerStagedAdditions && !isStagedModeExperiment;
	if (isStandardCtaFlow) {
		for (const { principal, role } of userPickerStagedAdditions) {
			map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: true });
		}
	}
	return map;
}
